import React from 'react';
import { Box, Button, Card, Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
	buildIncludedCategoriesSummary,
	calculateUtility,
	GetPAXTotalsResponse,
	separadorDeMiles,
} from '../../helpers';
import { DestinationSimple, HotelOptionsSimple, QuotationServiceSimple } from '../../types';
import { HotelDetailCardMultiDestination } from '../HotelDetailCardMultiDestination';
import { RoomPricesSimplePreview } from './RoomPricesSimplePreview';
import { useSelector } from 'react-redux';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useAgencyBranding } from '../../branding';
import { RootState } from 'store';
import { countryWAPMapping } from 'constants/constants';

const useStyles = makeStyles((theme) => ({
	paper: {
		marginTop: theme.spacing(8),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'left',
	},
	form: {
		width: '100%', // Fix IE 11 issue.
		marginTop: theme.spacing(3),
	},
	table: {
		minWidth: 650,
	},
	formControl: {
		marginTop: -3,
		width: '100%',
	},
	formGroup: {
		marginTop: 14,
	},
	optionTableHolder: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(3),
	},
	utility: {
		color: theme.palette.secondary.main,
		fontWeight: 'bold',
	},
	sign: {
		color: theme.palette.primary.main,
		fontWeight: 'bold',
	},
}));
interface Props {
	destinations: DestinationSimple[];
	paxTotals: GetPAXTotalsResponse;
	showUtility: boolean;
	markup: number | null;
	totalFlights: number;
	flightIndexSelected: number | null;
	exchangeRate: number;
	localCurrency: string;
	showLocalCurrency?: boolean;
	quotationId: number;
	isExternal?: boolean;
	/**
	 * Adicionales elegidos por el cliente en el preview (opcionales por destino y
	 * asistencia al viajero). Son totales absolutos en USD para todo el grupo, no
	 * por pasajero ni por habitación, así que se suman una sola vez a cada opción.
	 */
	selectedOptionalsTotalUSD?: number;
	selectedAssistanceTotalUSD?: number;
	selectedAssistanceName?: string;
	/** Servicios manuales: también van en "Incluye", así que cuentan en el resumen. */
	manualServices?: QuotationServiceSimple[];
	/** Opción elegida por destino (índice absoluto en destination.hotelOptions). */
	selectedHotelByDestination?: Record<number, number>;
	onSelectHotelOption?: (destinationIndex: number, optionIndex: number) => void;
}

/**
 * Margen bruto de una opción: suma de los márgenes de cada habitación. Sumar
 * habitación por habitación (en vez de aplicar el markup al total) garantiza
 * que el número coincida exactamente con la suma de los "mb:" que se ven
 * arriba en cada card.
 */
const optionGrossMargin = (option: { rooms: { totalPrice: number }[] }, markup: number | null): number =>
	option.rooms.reduce((total, room) => total + calculateUtility(room.totalPrice, markup), 0);

export const QuotationFinalPricePreview = (props: Props): JSX.Element => {
	const classes = useStyles();

	const { auth } = useSelector((state: RootState) => state);
	const branding = useAgencyBranding(props.isExternal);
	const countryWAP = countryWAPMapping[auth.country] || '59897683318';

	const {
		destinations,
		paxTotals,
		totalFlights,
		flightIndexSelected,
		quotationId,
		selectedOptionalsTotalUSD = 0,
		selectedAssistanceTotalUSD = 0,
		selectedAssistanceName,
	} = props;

	const additionalTotalUSD = selectedOptionalsTotalUSD + selectedAssistanceTotalUSD;

	const formatUSD = (valueUSD: number) => `USD ${separadorDeMiles(valueUSD)}`;
	const formatLocal = (valueUSD: number) =>
		`${props.localCurrency} ${separadorDeMiles(Math.round(valueUSD * props.exchangeRate))}`;

	// Mismo texto que usa el multi-destino, para que el mensaje de WhatsApp
	// no dependa del tipo de cotización.
	const additionalMessage =
		additionalTotalUSD > 0
			? `\nAdicionales seleccionados:\n${
					selectedOptionalsTotalUSD > 0 ? `* Opcionales agregados: USD ${selectedOptionalsTotalUSD}.\n` : ''
			  }${
					selectedAssistanceTotalUSD > 0
						? `* Asistencia ${selectedAssistanceName || ''}: USD ${selectedAssistanceTotalUSD}.`
						: ''
			  }`
			: '';

	// Preserva el índice absoluto dentro de destination.hotelOptions: la
	// selección se guarda contra ese índice para sobrevivir al filtrado por
	// vuelo (las opciones visibles cambian cuando cambia el vuelo elegido).
	const hotelOptionsToShow = (
		hotelOptions: HotelOptionsSimple[],
	): { option: HotelOptionsSimple; absoluteIndex: number }[] => {
		const withIndex = hotelOptions.map((option, absoluteIndex) => ({ option, absoluteIndex }));
		if (totalFlights > 1 && flightIndexSelected != null) {
			return withIndex.filter(({ option }) => option.flightIndex === flightIndexSelected);
		}
		return withIndex;
	};

	return (
		<>
			{destinations.map((destinationItem, destinationIndex) => {
				// Categorías incluidas: los servicios de ESTE destino (que es lo que
				// está dentro del precio de sus opciones) más los manuales.
				const includedSummary = buildIncludedCategoriesSummary({
					hasFlight: props.totalFlights > 0,
					hasAccommodation: true,
					serviceNames: [
						...(destinationItem.services || []).map((service) => service.name),
						...(props.manualServices || []).map((service) => service.name),
					],
				});
				return hotelOptionsToShow(destinationItem.hotelOptions).map(({ option, absoluteIndex }, index) => {
					const optionMessage = `Hola, quiero avanzar con la opción ${
						index + 1
					} de la cotización n* ${quotationId} con el hotel ${option?.details?.name}.${additionalMessage}`;
					const encodeOptionMessage = encodeURIComponent(optionMessage);
					const moveForwardLink = `https://wa.me/${countryWAP}?text=${encodeOptionMessage}`;

					const basePackageTotalUSD = option.rooms.reduce((sum, room) => sum + room.totalPrice, 0);
					const updatedPackageTotalUSD = basePackageTotalUSD + additionalTotalUSD;

					const isSelected = props.selectedHotelByDestination?.[destinationIndex] === absoluteIndex;
					const handleSelect = () => props.onSelectHotelOption?.(destinationIndex, absoluteIndex);

					return (
						<div key={absoluteIndex} className={classes.optionTableHolder}>
							<Box display="flex" alignItems="baseline" gap={1} mb={1}>
								<Typography component="h1" variant="h6" fontWeight={800} color="primary">
									Opción {index + 1}
								</Typography>
								{isSelected && (
									<Typography variant="caption" fontWeight={800} sx={{ color: '#2E7D32' }}>
										· Tu selección
									</Typography>
								)}
							</Box>

							{/* Card de hotel (la misma del rediseño multi) + habitaciones. En
							    desktop van lado a lado; en mobile apiladas. */}
							<Grid container spacing={1.5} alignItems="stretch">
								<Grid item xs={12} md={5} lg={4}>
									<HotelDetailCardMultiDestination
										hotelOption={option}
										isSelected={isSelected}
										handleSelectHotel={handleSelect}
										isExternal={props.isExternal}
										paxTotals={paxTotals}
									/>
								</Grid>
								<Grid item xs={12} md={7} lg={8}>
									<RoomPricesSimplePreview
										rooms={option.rooms}
										paxTotals={paxTotals}
										exchangeRate={props.exchangeRate}
										localCurrency={props.localCurrency}
										showLocalCurrency={props.showLocalCurrency}
										isExternal={props.isExternal}
										showUtility={props.showUtility}
										markup={props.markup}
										includedSummary={includedSummary}
									/>
								</Grid>
							</Grid>
							{/* Margen bruto TOTAL de la opción (suma de las habitaciones), sólo para
							    el agente. Se suma habitación por habitación en vez de aplicar el
							    markup al total, así este número es exactamente la suma de los "mb:"
							    de arriba. */}
							{props.showUtility && props.markup != null && (
								<Card
									sx={{
										marginTop: '8px',
										padding: '10px 14px',
										borderRadius: '12px',
										border: '1px solid rgba(49, 25, 75, 0.10)',
										display: 'flex',
										alignItems: 'baseline',
										justifyContent: 'space-between',
										gap: 1,
									}}
								>
									<Typography variant="body2" fontWeight={800} color="primary">
										Margen bruto de la opción {index + 1}
									</Typography>
									<Typography variant="body2" fontWeight={900} sx={{ color: 'info.dark' }}>
										mb: {optionGrossMargin(option, props.markup)}
									</Typography>
								</Card>
							)}
							{additionalTotalUSD > 0 && (
								<Card
									sx={{
										marginTop: '8px',
										padding: '12px 14px',
										borderRadius: '12px',
										border: '1px solid rgba(49, 25, 75, 0.10)',
										boxShadow: '0 6px 18px rgba(49, 25, 75, 0.08)',
									}}
								>
									<Typography variant="caption" color="text.secondary" fontWeight={700}>
										Total actualizado para el grupo
									</Typography>
									<Box display="flex" alignItems="baseline" justifyContent="space-between" gap={1} mt={0.25}>
										<Typography variant="h6" color="primary" fontWeight={900} lineHeight={1.1}>
											{formatUSD(updatedPackageTotalUSD)}
										</Typography>
										{props.showLocalCurrency && (
											<Typography variant="caption" color="text.secondary" fontWeight={800} textAlign="right">
												{formatLocal(updatedPackageTotalUSD)}
											</Typography>
										)}
									</Box>
									<Box mt={1} display="grid" gap={0.5}>
										<Box display="flex" justifyContent="space-between" gap={1}>
											<Typography variant="caption" color="text.secondary" fontWeight={700}>
												Paquete base
											</Typography>
											<Typography variant="caption" color="text.secondary" fontWeight={800}>
												{formatUSD(basePackageTotalUSD)}
											</Typography>
										</Box>
										{selectedOptionalsTotalUSD > 0 && (
											<Box display="flex" justifyContent="space-between" gap={1}>
												<Typography variant="caption" color="text.secondary" fontWeight={700}>
													Opcionales agregados
												</Typography>
												<Typography variant="caption" color="secondary" fontWeight={900}>
													+ {formatUSD(selectedOptionalsTotalUSD)}
												</Typography>
											</Box>
										)}
										{selectedAssistanceTotalUSD > 0 && (
											<Box display="flex" justifyContent="space-between" gap={1}>
												<Typography variant="caption" color="text.secondary" fontWeight={700}>
													Asistencia {selectedAssistanceName}
												</Typography>
												<Typography variant="caption" color="secondary" fontWeight={900}>
													+ {formatUSD(selectedAssistanceTotalUSD)}
												</Typography>
											</Box>
										)}
									</Box>
								</Card>
							)}
							{!props.showUtility && (
								<Card sx={{ display: 'flex', marginTop: '8px' }}>
									<Box sx={{ display: 'flex', flexDirection: 'column', padding: '8px', width: '100%' }}>
										<Typography variant="body2" fontSize={12}>
											{' '}
											{paxTotals.child > 0
												? '* Son considerados niños aquellos pasajeros que tengan entre 2 y 11 años al momento de culminar el viaje'
												: ''}{' '}
										</Typography>
										<Typography variant="body2" fontSize={12}>
											{' '}
											{paxTotals.infants > 0
												? '* Son considerados bebés aquellos pasajeros que tengan entre 0 y 1 año al momento de culminar el viaje'
												: ''}{' '}
										</Typography>
										{/* CTA por opción con la marca (no verde genérico) y diciendo
										    exactamente qué hace: abre WhatsApp con el asesor. */}
										<Button
											rel="noopener noreferrer"
											target="_blank"
											component="a"
											href={moveForwardLink}
											startIcon={<WhatsAppIcon />}
											sx={{
												margin: '8px',
												width: 'fit-content',
												alignSelf: 'self-end',
												borderRadius: '999px',
												textTransform: 'none',
												fontWeight: 800,
												color: branding.accent,
												borderColor: branding.accent,
												'&:hover': { borderColor: branding.accent, backgroundColor: 'rgba(49, 25, 75, 0.04)' },
											}}
											size="medium"
											variant="outlined"
										>
											Pedir la opción {index + 1} por WhatsApp
										</Button>
									</Box>
								</Card>
							)}
						</div>
					);
				});
			})}
		</>
	);
};
